import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { AUTH_CONFIG, AUTH_LOGIN_SCOPES_ARRAY, AUTH_SCOPES, AUTH_TOKEN_SCOPES_ARRAY } from './authConfig';
import { getMsalRedirectError } from './msalClient';
import { decodeJwtPayload, getPermissionsFromClaims } from './tokenClaims';

const AuthAccessContext = createContext(null);
const ENTRA_RETURN_TO_KEY = 'execEye.entra.returnTo';

const loginErrorCodes = new Set([
  'consent_required',
  'interaction_required',
  'invalid_grant',
  'login_required',
  'missing_refresh_token'
]);

function shouldRedirectToLogin(error) {
  return loginErrorCodes.has(error?.error) || loginErrorCodes.has(error?.errorCode);
}

export function isLoginRedirectRequired(error) {
  return shouldRedirectToLogin(error);
}

function getDefaultReturnTo() {
  return `${window.location.pathname}${window.location.search}`;
}

function getAbsoluteReturnTo(returnTo = getDefaultReturnTo()) {
  return new URL(returnTo || '/', window.location.origin).toString();
}

function rememberReturnTo(returnTo = getDefaultReturnTo()) {
  try {
    window.sessionStorage.setItem(ENTRA_RETURN_TO_KEY, returnTo || '/');
  } catch (error) {
    // Session storage may be blocked; falling back to the default route is fine.
  }
}

export function consumeEntraReturnTo() {
  try {
    const returnTo = window.sessionStorage.getItem(ENTRA_RETURN_TO_KEY);
    window.sessionStorage.removeItem(ENTRA_RETURN_TO_KEY);
    return returnTo || '';
  } catch (error) {
    return '';
  }
}

function AuthAccessStateProvider({ children, authSession }) {
  const {
    autoRedirectOnClaimError = true,
    error,
    getAccessToken,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    redirectToLogin,
    user
  } = authSession;
  const [permissions, setPermissions] = useState([]);
  const [claims, setClaims] = useState({});
  const [isAuthorizing, setIsAuthorizing] = useState(true);
  const [authorizationError, setAuthorizationError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadClaims = async () => {
      if (isLoading) return;

      if (!isAuthenticated) {
        if (isMounted) {
          setPermissions([]);
          setClaims({});
          setAuthorizationError(null);
          setIsAuthorizing(false);
        }
        return;
      }

      setIsAuthorizing(true);
      setAuthorizationError(null);

      try {
        const token = await getAccessToken();
        const nextClaims = decodeJwtPayload(token);
        const nextPermissions = getPermissionsFromClaims(nextClaims, {
          permissions: AUTH_CONFIG.permissions,
          userRoleAliases: AUTH_CONFIG.roleAliases.user,
          adminRoleAliases: AUTH_CONFIG.roleAliases.admin,
          trustScopeClaims: AUTH_CONFIG.trustScopeClaims
        });

        if (isMounted) {
          setClaims(nextClaims);
          setPermissions(nextPermissions);
          setAuthorizationError(null);
        }
      } catch (error) {
        if (autoRedirectOnClaimError && shouldRedirectToLogin(error)) {
          await redirectToLogin();
          return;
        }

        if (isMounted) {
          setClaims({});
          setPermissions([]);
          setAuthorizationError(error);
        }
      } finally {
        if (isMounted) {
          setIsAuthorizing(false);
        }
      }
    };

    loadClaims();

    return () => {
      isMounted = false;
    };
  }, [autoRedirectOnClaimError, getAccessToken, isAuthenticated, isLoading, redirectToLogin]);

  const hasPermission = useCallback((permission) => {
    if (!permission) return true;
    if (permissions.includes(permission)) return true;

    return permission === AUTH_CONFIG.permissions.user
      && permissions.includes(AUTH_CONFIG.permissions.admin);
  }, [permissions]);

  const value = useMemo(() => ({
    authorizationError,
    claims,
    error,
    getAccessToken,
    hasPermission,
    isAuthorizing,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    permissions,
    redirectToLogin,
    user
  }), [
    authorizationError,
    claims,
    error,
    getAccessToken,
    hasPermission,
    isAuthorizing,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    permissions,
    redirectToLogin,
    user
  ]);

  return (
    <AuthAccessContext.Provider value={value}>
      {children}
    </AuthAccessContext.Provider>
  );
}

function Auth0AuthAccessProvider({ children }) {
  const {
    error,
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithRedirect: auth0LoginWithRedirect,
    logout: auth0Logout,
    user
  } = useAuth0();

  const getAccessToken = useCallback(async () => (
    getAccessTokenSilently({
      authorizationParams: {
        audience: AUTH_CONFIG.audience,
        scope: AUTH_SCOPES
      }
    })
  ), [getAccessTokenSilently]);

  const redirectToLogin = useCallback(async (returnTo = getDefaultReturnTo()) => {
    await auth0LoginWithRedirect({
      appState: { returnTo }
    });
  }, [auth0LoginWithRedirect]);

  const loginWithRedirect = useCallback(async () => redirectToLogin(), [redirectToLogin]);

  const logout = useCallback(async () => {
    await auth0Logout({ logoutParams: { returnTo: `${window.location.origin}/` } });
  }, [auth0Logout]);

  return (
    <AuthAccessStateProvider
      authSession={{
        error,
        getAccessToken,
        isAuthenticated,
        isLoading,
        loginWithRedirect,
        logout,
        redirectToLogin,
        user
      }}
    >
      {children}
    </AuthAccessStateProvider>
  );
}

function EntraAuthAccessProvider({ children }) {
  const { accounts, inProgress, instance } = useMsal();
  const [redirectError] = useState(getMsalRedirectError);
  const isAuthenticated = useIsAuthenticated();
  const activeAccount = instance.getActiveAccount() || accounts[0] || null;
  const isLoading = inProgress !== InteractionStatus.None;

  useEffect(() => {
    if (!instance.getActiveAccount() && accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [accounts, instance]);

  const getAccount = useCallback(() => instance.getActiveAccount() || accounts[0] || null, [accounts, instance]);

  const redirectToLogin = useCallback(async (returnTo = getDefaultReturnTo()) => {
    rememberReturnTo(returnTo);
    const extraQueryParameters = AUTH_CONFIG.entra.domainHint
      ? { domain_hint: AUTH_CONFIG.entra.domainHint }
      : undefined;
    const loginRequest = {
      scopes: AUTH_LOGIN_SCOPES_ARRAY,
      extraQueryParameters,
      redirectStartPage: getAbsoluteReturnTo(returnTo)
    };

    if (AUTH_CONFIG.entra.prompt) {
      loginRequest.prompt = AUTH_CONFIG.entra.prompt;
    }

    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const loginWithRedirect = useCallback(async () => redirectToLogin(), [redirectToLogin]);

  const getAccessToken = useCallback(async () => {
    const account = getAccount();

    if (!account) {
      const error = new Error('Microsoft sign-in has not created an active ExecEye session. Please use the Sign in button again.');
      error.errorCode = 'exec_eye_no_active_account';
      throw error;
    }

    try {
      const response = await instance.acquireTokenSilent({
        account,
        scopes: AUTH_TOKEN_SCOPES_ARRAY
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError || shouldRedirectToLogin(error)) {
        const tokenError = new Error('Microsoft sign-in completed, but ExecEye could not obtain an API access token silently. Please ask IT to confirm the SPA has delegated access to the ExecEye API scope and that your user or group is assigned to an ExecEye app role.');
        tokenError.errorCode = 'exec_eye_api_token_interaction_required';
        tokenError.originalError = error;
        throw tokenError;
      }

      throw error;
    }
  }, [getAccount, instance]);

  const logout = useCallback(async () => {
    await instance.logoutRedirect({
      account: getAccount(),
      postLogoutRedirectUri: `${window.location.origin}/`
    });
  }, [getAccount, instance]);

  const user = activeAccount ? {
    email: activeAccount.username || '',
    name: activeAccount.name || activeAccount.username || ''
  } : null;

  return (
    <AuthAccessStateProvider
      authSession={{
        error: redirectError,
        autoRedirectOnClaimError: false,
        getAccessToken,
        isAuthenticated,
        isLoading,
        loginWithRedirect,
        logout,
        redirectToLogin,
        user
      }}
    >
      {children}
    </AuthAccessStateProvider>
  );
}

export function AuthAccessProvider({ children }) {
  if (AUTH_CONFIG.isEntra) {
    return <EntraAuthAccessProvider>{children}</EntraAuthAccessProvider>;
  }

  return <Auth0AuthAccessProvider>{children}</Auth0AuthAccessProvider>;
}

export function useAuthAccess() {
  const context = useContext(AuthAccessContext);
  if (!context) {
    throw new Error('useAuthAccess must be used inside AuthAccessProvider.');
  }

  return context;
}
